/**
 * Single source of truth for runtime game data.
 * Each JSON file under this directory has a zod schema; on load, we validate.
 * On schema failure at module-load time, the page refuses to mount — fail loud.
 */
import { z } from 'zod';
import itemsJson from './items.json';
import skillsJson from './skills.json';
import monstersJson from './monsters.json';
import zonesJson from './zones.json';
import npcsJson from './npcs.json';
import questsJson from './quests.json';
import voiceJson from './voice.json';
import {
  EQUIP_SLOT_IDS,
  ITEM_IDS,
  NPC_IDS,
  parseMonstersCatalogue,
  parseNpcsCatalogue,
  parseSkillsCatalogue,
} from './catalogues';

export { EQUIP_SLOT_IDS, ITEM_IDS, MONSTER_IDS, NPC_IDS, SKILL_IDS } from './catalogues';
export type { EquipSlotId } from './catalogues';

// ─── Item IDs (canonical) ─────────────────────────────────────────────
// ─── Schemas ───────────────────────────────────────────────────────────
const ItemIdSchema = z.enum(ITEM_IDS);

const ItemDefSchema = z.object({
  name: z.string().min(1),
  category: z.enum(['gathering', 'consumable', 'currency', 'equipment']),
  tradeable: z.boolean(),
  stackable: z.boolean(),
  maxStack: z.number().int().positive(),
  value: z.number().int().nonnegative(),
  /** Optional equipment declaration: compatible slot + flat bonuses. */
  equip: z
    .object({
      slot: z.enum(EQUIP_SLOT_IDS),
      bonuses: z
        .object({
          attack: z.number().int().optional(),
          defence: z.number().int().optional(),
          hp: z.number().int().optional(),
        })
        .strict(),
    })
    .strict()
    .optional(),
});
const ItemsSchema = z.object({
  schemaVersion: z.literal(1),
  items: z.record(ItemIdSchema, ItemDefSchema),
});

const ZoneSchema = z.object({
  id: z.string(),
  name: z.string(),
  tier: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  monsterIds: z.array(z.string()),
  boss: z.string().nullable(),
  flavor: z.string(),
});
const ZonesSchema = z.object({
  schemaVersion: z.literal(1),
  zones: z.array(ZoneSchema),
});

const QuestObjectiveSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('kill'), monsterId: z.string(), count: z.number().int().positive() }),
]);
const QuestSchema = z.object({
  id: z.string(),
  name: z.string(),
  giver: z.enum(NPC_IDS),
  tier: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  kind: z.string(),
  objective: QuestObjectiveSchema,
  reward: z.object({
    xp: z.record(z.string(), z.number().int().nonnegative()),
    items: z.array(z.string()),
  }),
  unlocks: z.array(z.string()),
  flavor: z.string(),
});
const QuestsSchema = z.object({
  schemaVersion: z.literal(1),
  quests: z.array(QuestSchema),
});

const VoiceSchema = z.object({
  schemaVersion: z.literal(1),
  toasts: z.record(z.string(), z.string()),
});

// ─── Validated + typed exports ────────────────────────────────────────
function parseOrThrow<T>(schema: z.ZodType<T>, data: unknown, file: string): T {
  const result = schema.safeParse(data);
  if (!result.success) {
    const lines = result.error.issues.map((i) => `  - ${i.path.join('.')}: ${i.message}`).join('\n');
    throw new Error(`[Hermanity Idle] ${file} failed validation:\n${lines}\n\nAborting — fix the JSON or update the schema.`);
  }
  return result.data;
}

export const ITEMS = parseOrThrow(ItemsSchema, itemsJson, 'items.json');
export const SKILLS_DATA = parseSkillsCatalogue(skillsJson);
export const MONSTERS_DATA = parseMonstersCatalogue(monstersJson);
export const ZONES = parseOrThrow(ZonesSchema, zonesJson, 'zones.json');
export const NPCS = parseNpcsCatalogue(npcsJson);
export const QUESTS = parseOrThrow(QuestsSchema, questsJson, 'quests.json');
export const TOASTS = parseOrThrow(VoiceSchema, voiceJson, 'voice.json');

// ─── Convenience indexes ─────────────────────────────────────────────
export const ITEMS_BY_ID = ITEMS.items;
export const SKILLS_BY_ID = new Map(SKILLS_DATA.skills.map((s) => [s.id, s] as const));
export const MONSTERS_BY_ID = new Map(MONSTERS_DATA.monsters.map((m) => [m.id, m] as const));
export const NPCS_BY_ID = new Map(NPCS.npcs.map((n) => [n.id, n] as const));
export const QUESTS_BY_ID = new Map(QUESTS.quests.map((q) => [q.id, q] as const));

export function getNpc(id: string) {
  const npc = NPCS_BY_ID.get(id as (typeof NPC_IDS)[number]);
  if (!npc) {
    throw new Error(`[Hermanity Idle] NPC catalogue has no entry for "${id}".`);
  }
  return npc;
}
